import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type FocusEvent,
  type HTMLAttributes,
} from "react"
import {
  getResourceTransferPayload,
  hasResourceTransferData,
} from "./resourceUpload"

export const RESOURCE_UPLOAD_PASTE_PRIORITY = {
  HOME: 0,
  FIELD: 10,
  MODAL: 20,
} as const

export type PasteActivation = "always" | "focus" | "focus-or-hover"

export type ResourceUploadSurfaceProps = Pick<
  HTMLAttributes<HTMLElement>,
  | "onBlurCapture"
  | "onDragEnter"
  | "onDragLeave"
  | "onDragOver"
  | "onDrop"
  | "onFocusCapture"
  | "onMouseEnter"
  | "onMouseLeave"
>

type UseResourceUploadInteractionsOptions = {
  accept?: string
  allowRemoteImage: boolean
  disabled?: boolean
  enabled?: boolean
  multiple?: boolean
  pasteActivation: PasteActivation
  pastePriority: number
  onFiles: (files: File[]) => void
  onRemoteImageUrl: (url: string) => void
}

type PasteOwner = {
  handlePaste: (event: ClipboardEvent) => void
  order: number
  priority: number
}

const PASTE_OWNERS = new Map<symbol, PasteOwner>()
let pasteOwnerOrder = 0
let isPasteListenerActive = false

const handleWindowPaste = (event: ClipboardEvent) => {
  const owner = Array.from(PASTE_OWNERS.values()).reduce<
    PasteOwner | undefined
  >((currentOwner, nextOwner) => {
    if (!currentOwner) return nextOwner
    if (nextOwner.priority > currentOwner.priority) return nextOwner
    if (
      nextOwner.priority === currentOwner.priority &&
      nextOwner.order > currentOwner.order
    ) {
      return nextOwner
    }

    return currentOwner
  }, undefined)

  owner?.handlePaste(event)
}

const syncPasteListener = () => {
  if (PASTE_OWNERS.size > 0 && !isPasteListenerActive) {
    window.addEventListener("paste", handleWindowPaste, true)
    isPasteListenerActive = true
    return
  }

  if (PASTE_OWNERS.size === 0 && isPasteListenerActive) {
    window.removeEventListener("paste", handleWindowPaste, true)
    isPasteListenerActive = false
  }
}

const registerPasteOwner = (id: symbol, owner: Omit<PasteOwner, "order">) => {
  pasteOwnerOrder += 1
  PASTE_OWNERS.set(id, { ...owner, order: pasteOwnerOrder })
  syncPasteListener()

  return () => {
    PASTE_OWNERS.delete(id)
    syncPasteListener()
  }
}

export const useResourceUploadInteractions = ({
  accept,
  allowRemoteImage,
  disabled = false,
  enabled = true,
  multiple = false,
  pasteActivation,
  pastePriority,
  onFiles,
  onRemoteImageUrl,
}: UseResourceUploadInteractionsOptions) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isFocusedWithin, setIsFocusedWithin] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const pasteOwnerIdRef = useRef(Symbol("resource-upload-paste-owner"))
  const dragDepthRef = useRef(0)
  const onFilesRef = useRef(onFiles)
  const onRemoteImageUrlRef = useRef(onRemoteImageUrl)

  onFilesRef.current = onFiles
  onRemoteImageUrlRef.current = onRemoteImageUrl

  const dispatchTransfer = useCallback(
    (data: DataTransfer | null) => {
      const payload = getResourceTransferPayload(data, { allowRemoteImage })

      switch (payload.kind) {
        case "empty":
          return false
        case "files":
          onFilesRef.current(payload.files)
          break
        default:
          onRemoteImageUrlRef.current(payload.url)
      }

      return true
    },
    [allowRemoteImage],
  )

  const isPasteActive =
    enabled &&
    !disabled &&
    (pasteActivation === "always" ||
      (pasteActivation === "focus" && isFocusedWithin) ||
      (pasteActivation === "focus-or-hover" && (isFocusedWithin || isHovered)))

  useEffect(() => {
    if (!isPasteActive) return undefined

    return registerPasteOwner(pasteOwnerIdRef.current, {
      handlePaste: (event) => {
        if (!dispatchTransfer(event.clipboardData)) return

        event.preventDefault()
        event.stopImmediatePropagation()
      },
      priority: pastePriority,
    })
  }, [dispatchTransfer, isPasteActive, pastePriority])

  useEffect(() => {
    if (!disabled && enabled) return

    dragDepthRef.current = 0
    setIsDragging(false)
  }, [disabled, enabled])

  const hasTransferData = (data: DataTransfer | null) => {
    return hasResourceTransferData(data, { allowRemoteImage })
  }

  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    if (!hasTransferData(event.dataTransfer)) return

    event.preventDefault()
    event.stopPropagation()

    if (disabled || !enabled) return

    dragDepthRef.current += 1
    setIsDragging(true)
  }

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    if (!hasTransferData(event.dataTransfer)) return

    event.preventDefault()
    event.stopPropagation()
    event.dataTransfer.dropEffect = disabled || !enabled ? "none" : "copy"
  }

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!isDragging) return

    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)

    if (dragDepthRef.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    if (!hasTransferData(event.dataTransfer)) return

    event.preventDefault()
    event.stopPropagation()
    dragDepthRef.current = 0
    setIsDragging(false)

    if (disabled || !enabled) return

    dispatchTransfer(event.dataTransfer)
  }

  const handleFocusCapture = () => {
    setIsFocusedWithin(true)
  }

  const handleBlurCapture = (event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return

    setIsFocusedWithin(false)
  }

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files || [])

    event.currentTarget.value = ""

    if (disabled || !enabled || !files.length) return

    onFilesRef.current(files)
  }

  const openFileDialog = () => {
    if (disabled || !enabled) return

    inputRef.current?.click()
  }
  const surfaceProps: ResourceUploadSurfaceProps = {
    onBlurCapture: handleBlurCapture,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    onDrop: handleDrop,
    onFocusCapture: handleFocusCapture,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  }

  return {
    inputProps: {
      accept,
      disabled: disabled || !enabled,
      multiple,
      onChange: handleFileInputChange,
      ref: inputRef,
      type: "file" as const,
    },
    isDragging,
    openFileDialog,
    surfaceProps,
  }
}
